import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #interval = undefined;
  #touchStartX = 0;
  #touchEndX = 0;
  #continuousSourceHtml = '';
  #continuousResizeBound = false;
  #continuousResizeTimer = undefined;
  #onContinuousResize = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#setupTouchSupport();
    this.#setupContinuousScroll();

    if (!this.isContinuous) {
      this.current = 0;
      this.play();
    }
  }

  get isContinuous() {
    const style = this.getAttribute('data-scroll-style');
    return Boolean(style && style.includes('continuous'));
  }

  #setupContinuousScroll() {
    if (!this.isContinuous) return;

    const inner = this.querySelector('.announcement-bar__slides-inner');
    if (!inner) return;

    if (!this.#continuousSourceHtml) {
      const slides = [...inner.querySelectorAll('.announcement-bar__slide')];
      if (slides.length === 0) return;

      slides.forEach((slide) => this.#prepareContinuousSlide(slide));
      this.#continuousSourceHtml = inner.innerHTML;
    }

    // Wait a frame so content-width CSS is applied before measuring/cloning.
    requestAnimationFrame(() => {
      this.#buildContinuousTrack();
    });

    if (!this.#continuousResizeBound) {
      this.#continuousResizeBound = true;
      this.#onContinuousResize = () => {
        window.clearTimeout(this.#continuousResizeTimer);
        this.#continuousResizeTimer = window.setTimeout(() => this.#buildContinuousTrack(), 150);
      };
      window.addEventListener('resize', this.#onContinuousResize, { passive: true });
    }
  }

  #prepareContinuousSlide(slide) {
    slide.setAttribute('aria-hidden', 'false');
    slide.classList.add('is-active');
    slide.classList.remove('text-block--full-width');
    slide.style.setProperty('--width', 'max-content');
    slide.style.width = 'max-content';
    slide.style.minWidth = 'max-content';
    slide.style.maxWidth = 'none';
    slide.style.flex = '0 0 auto';
  }

  /**
   * Fill the track past the viewport, then duplicate once for a seamless -50% loop.
   */
  #buildContinuousTrack() {
    const inner = this.querySelector('.announcement-bar__slides-inner');
    if (!inner || !this.#continuousSourceHtml) return;

    // Pause animation while rebuilding so width measurements are stable.
    inner.style.animation = 'none';
    inner.innerHTML = this.#continuousSourceHtml;
    inner.dataset.cloned = 'false';

    [...inner.querySelectorAll('.announcement-bar__slide')].forEach((slide) => {
      this.#prepareContinuousSlide(slide);
    });

    const viewportWidth = Math.max(this.offsetWidth || 0, window.innerWidth || 0, 320);
    let guard = 0;
    let lastWidth = 0;

    while (inner.scrollWidth < viewportWidth + 80 && guard < 40) {
      inner.insertAdjacentHTML('beforeend', this.#continuousSourceHtml);
      [...inner.querySelectorAll('.announcement-bar__slide')].forEach((slide) => {
        this.#prepareContinuousSlide(slide);
      });

      // Stop if width is not growing (styles still forcing full-width).
      if (inner.scrollWidth <= lastWidth + 1) break;
      lastWidth = inner.scrollWidth;
      guard += 1;
    }

    const trackHtml = inner.innerHTML;
    inner.innerHTML = trackHtml + trackHtml;
    [...inner.querySelectorAll('.announcement-bar__slide')].forEach((slide) => {
      this.#prepareContinuousSlide(slide);
    });
    inner.dataset.cloned = 'true';
    inner.style.animation = '';
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    if (this.#onContinuousResize) {
      window.removeEventListener('resize', this.#onContinuousResize);
    }
    window.clearTimeout(this.#continuousResizeTimer);
    this.suspend();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  goTo(index) {
    this.current = index;
  }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    if (this.isContinuous) return false;
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    const totalSlides = slides.length;
    if (totalSlides === 0) return;

    let relativeIndex = current % totalSlides;
    if (relativeIndex < 0) {
      relativeIndex += totalSlides;
    }

    this.#current = relativeIndex;
    this.style.setProperty('--current-slide', `${relativeIndex}`);
    this.setAttribute('data-current-slide', `${relativeIndex}`);

    slides.forEach((slide, index) => {
      const isCurrent = index === relativeIndex;
      slide.setAttribute('aria-hidden', `${!isCurrent}`);
      slide.classList.toggle('is-active', isCurrent);
    });

    const dots = this.querySelectorAll('.announcement-bar__dot');
    dots.forEach((dot, index) => {
      const isCurrent = index === relativeIndex;
      dot.classList.toggle('is-active', isCurrent);
      dot.setAttribute('aria-current', isCurrent ? 'true' : 'false');
    });
  }

  #setupTouchSupport() {
    this.addEventListener('touchstart', (e) => {
      this.#touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      this.#touchEndX = e.changedTouches[0].screenX;
      this.#handleSwipe();
    }, { passive: true });
  }

  #handleSwipe() {
    const threshold = 30;
    const diff = this.#touchEndX - this.#touchStartX;
    if (Math.abs(diff) > threshold) {
      if (diff < 0) {
        this.next();
      } else {
        this.previous();
      }
    }
  }

  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
